import React, {FC, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {FormModelType, useForm} from 'reactjs-use-form'

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
} from '@mui/material'
import {useSnackbar} from 'notistack'
import * as Yup from 'yup'

import SubmitButton from '@progyny/components/Buttons/SubmitButton'
import CenteredContainer from '@progyny/components/CenteredContainer'
import {ResponsiveContainer} from '@progyny/components/ResponsiveContainer'
import {useSubmitFreshdeskTicketAnonymouslyMutation} from '@progyny/generated/graphql'
import {useErrorCodes} from '@progyny/hooks/useErrorCodes'

const BlockedAccount: FC = () => {
  const {t} = useTranslation()
  const [showSupportForm, setShowSupportForm] = useState(false)
  const [formHasErrors, setFormHasErrors] = useState(false)
  const [request, {loading}] = useSubmitFreshdeskTicketAnonymouslyMutation()
  const {enqueueSnackbar} = useSnackbar()
  const getErrorMessage = useErrorCodes()

  const emailValidation = Yup.string().email().nullable()

  const formModel: FormModelType = {
    privateEmail: {
      value: '',
      required: true,
      validator: (email) => {
        if (!emailValidation.isValidSync(email)) {
          return getErrorMessage('invalid-email')
        } else return ''
      },
    },
    body: {
      value: '',
      required: true,
    },
  }

  const {
    values,
    errors,
    isDisabled,
    isSubmitted,
    handleOnSubmit,
    handleOnChange,
  } = useForm(formModel, handleSubmit)

  function handleSubmit() {
    request({
      variables: {
        email: values.privateEmail,
        body: values.body,
      },
    })
      .then((res) => {
        if (res.data?.submitFreshdeskTicketAnonymously?.errors) {
          const errors = res.data?.submitFreshdeskTicketAnonymously?.errors
          const message = errors[0]?.messages[0]
          setFormHasErrors((prevState) => !prevState)
          return enqueueSnackbar({message: message, variant: 'error'})
        }
      })
      .catch((error) => {
        if (error.networkError && error.networkError.statusCode === 429) {
          enqueueSnackbar({
            message: t('too_many_requests'),
            variant: 'warning',
          })
        } else {
          return console.error(error)
        }
      })
  }

  return (
    <>
      {!showSupportForm && (
        <ResponsiveContainer>
          <CenteredContainer>
            <Box mx='auto' maxWidth={524}>
              <Typography align='center' variant='h4'>
                {t('blocked_account_this_progyny_account_has_been')}
              </Typography>
              <Box mt={4}>
                <Typography>
                  {t('blocked_account_it_appears_that_consent_to')}
                </Typography>
              </Box>
              <Box mt={2}>
                <Typography>
                  {t('blocked_account_we_ve_sent_you_email')}
                </Typography>
              </Box>
              <Box
                mt={5}
                display={'flex'}
                flexDirection={'row'}
                justifyContent={'center'}
              >
                <Button
                  variant={'contained'}
                  onClick={() => setShowSupportForm(true)}
                >
                  Contact support
                </Button>
              </Box>
            </Box>
          </CenteredContainer>
        </ResponsiveContainer>
      )}
      {showSupportForm && (
        <ResponsiveContainer>
          <CenteredContainer>
            <Box mx='auto' maxWidth={386}>
              <Typography align='center' variant='h4'>
                {`Progyny ${t('support')}`}
              </Typography>
              <Box mt={4}>
                <Typography>
                  <Trans i18nKey='blocked_account_you_are_contacting_us_because' />
                </Typography>
              </Box>
              <Box>
                <form onSubmit={handleOnSubmit}>
                  <Box mt={2}>
                    <FormControl
                      variant={'outlined'}
                      margin={'dense'}
                      fullWidth
                    >
                      <TextField
                        inputProps={{'aria-label': 'Email'}}
                        name='privateEmail'
                        variant='outlined'
                        size='small'
                        fullWidth
                        label={t('profile_personal_info_private_email_label')}
                        placeholder={t(
                          'profile_personal_info_private_email_label'
                        )}
                        error={errors.privateEmail.hasError}
                        helperText={t(errors.privateEmail.message)}
                        value={values.privateEmail}
                        onChange={handleOnChange}
                      />
                    </FormControl>
                  </Box>
                  <Box mt={2}>
                    <TextField
                      inputProps={{'aria-label': 'Message'}}
                      name='body'
                      multiline
                      minRows={6}
                      label={t('help_contact_explanation_of_your_issue')}
                      placeholder={t('help_contact_explanation_of_your_issue')}
                      error={errors.body.hasError}
                      helperText={t(errors.body.message)}
                      value={values.body}
                      onChange={handleOnChange}
                      fullWidth
                      variant='outlined'
                      size='small'
                    />
                  </Box>
                  <Box
                    mt={5}
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'center'}
                  >
                    {isSubmitted && !formHasErrors ? (
                      <Alert severity='success'>
                        <AlertTitle>
                          {t('blocked_account_thanks_contact')}
                        </AlertTitle>
                        {t('blocked_account_your_message_has_been_received')}
                      </Alert>
                    ) : (
                      <SubmitButton
                        type='submit'
                        disabled={isDisabled}
                        loading={loading}
                      >
                        {t('blocked_account_send_email')}
                      </SubmitButton>
                    )}
                  </Box>
                </form>
              </Box>
            </Box>
          </CenteredContainer>
        </ResponsiveContainer>
      )}
    </>
  )
}
export default BlockedAccount
